import React, { useEffect, useRef } from 'react';
import '../static/css/Loading.css';

function Loading({ onDone }) {
  const flexRef = useRef(null);
  const barFillRef = useRef(null);
  const canvasRef = useRef(null);
  const activeRef = useRef(true);
  const doneRef = useRef(false);

  useEffect(() => {
    const flex = flexRef.current;
    const barFill = barFillRef.current;
    const canvas = canvasRef.current;
    if (!flex || !barFill || !canvas) return undefined;

    const ctx = canvas.getContext('2d');
    activeRef.current = true;

    // timings — reduced so 3 cycles complete faster (~8s total)
    const LETTER_DUR = 240;
    const LETTER_GAP = 70;
    const HOLD_TIME = 500;
    const LETTER_OUT_DUR = 200;
    const LETTER_OUT_GAP = 50;
    const CYCLE_PAUSE = 200;

    const FULL = 'Vanilson Souza';
    const nonSpaces = [...FULL].filter((c) => c !== ' ').length;
    const ONE_CYCLE =
      nonSpaces * LETTER_GAP +
      LETTER_DUR +
      HOLD_TIME +
      nonSpaces * LETTER_OUT_GAP +
      LETTER_OUT_DUR +
      CYCLE_PAUSE;
    const BAR_DUR = ONE_CYCLE * 3;

    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    const easeInExpo = (t) => (t === 0 ? 0 : Math.pow(2, 10 * t - 10));

    const particles = [];
    let prevPct = 0;
    let barStart = null;
    let barRaf = null;

    function resizeCanvas() {
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = 34;
    }

    function spawnAt(pct) {
      const x = (pct / 100) * canvas.width;
      for (let i = 0; i < 4; i += 1) {
        particles.push({
          x,
          y: 17,
          vx: (Math.random() - 0.5) * 1.6,
          vy: -(Math.random() * 1.1 + 0.3),
          r: Math.random() * 1.2 + 0.3,
          a: 0.65,
        });
      }
    }

    function tickParticles() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.a -= 0.018;
        if (p.a <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = p.a;
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    function animEl(el, x0, x1, a0, a1, dur, easeFn, cb) {
      let start = null;
      let rafId = null;
      function step(ts) {
        if (!start) start = ts;
        const raw = Math.min((ts - start) / dur, 1);
        const e = easeFn(raw);
        el.style.transform = `translateX(${x0 + (x1 - x0) * e}px)`;
        el.style.opacity = `${a0 + (a1 - a0) * e}`;
        if (raw < 1) {
          rafId = requestAnimationFrame(step);
        } else {
          el.style.transform = `translateX(${x1}px)`;
          el.style.opacity = `${a1}`;
          if (cb) cb();
        }
      }
      rafId = requestAnimationFrame(step);
      return () => cancelAnimationFrame(rafId);
    }

    function buildLetters(W) {
      flex.innerHTML = '';
      const els = [];
      [...FULL].forEach((ch) => {
        if (ch === ' ') {
          const s = document.createElement('span');
          s.className = 'ld-space';
          flex.appendChild(s);
          els.push(null);
          return;
        }
        const s = document.createElement('span');
        s.className = 'ld-letter';
        s.textContent = ch;
        s.style.opacity = '0';
        s.style.transform = `translateX(${-W}px)`;
        flex.appendChild(s);
        els.push(s);
      });
      return els.filter(Boolean);
    }

    const timers = [];
    function addTimer(fn, ms) {
      const id = setTimeout(fn, ms);
      timers.push(id);
      return id;
    }

    function nameLoop() {
      if (!activeRef.current) return;
      const scene = canvas.closest('.ld-scene');
      const W = (scene ? scene.offsetWidth : window.innerWidth) + 60;
      const els = buildLetters(W);
      let inDone = 0;

      els.forEach((el, i) => {
        const order = els.length - 1 - i;
        addTimer(() => {
          if (!activeRef.current) return;
          animEl(el, -W, 0, 0, 1, LETTER_DUR, easeOutExpo, () => {
            inDone += 1;
            if (inDone === els.length) {
              addTimer(slideOut, HOLD_TIME);
            }
          });
        }, order * LETTER_GAP);
      });

      function slideOut() {
        if (!activeRef.current) return;
        els.forEach((el, i) => {
          const order = els.length - 1 - i;
          addTimer(() => {
            if (!activeRef.current) return;
            animEl(el, 0, W, 1, 0, LETTER_OUT_DUR, easeInExpo);
          }, order * LETTER_OUT_GAP);
        });
        const totalOut = (els.length - 1) * LETTER_OUT_GAP + LETTER_OUT_DUR;
        addTimer(() => {
          if (activeRef.current) nameLoop();
        }, totalOut + CYCLE_PAUSE);
      }
    }

    function drain() {
      tickParticles();
      if (particles.length > 0) {
        barRaf = requestAnimationFrame(drain);
      } else if (!doneRef.current) {
        doneRef.current = true;
        if (typeof onDone === 'function') onDone();
      }
    }

    function barTick(ts) {
      if (!activeRef.current) return;
      if (!barStart) barStart = ts;
      const raw = Math.min((ts - barStart) / BAR_DUR, 1);
      const ease = raw < 0.5 ? 2 * raw * raw : -1 + (4 - 2 * raw) * raw;
      const pct = ease * 100;
      barFill.style.width = `${pct}%`;
      if (pct - prevPct > 0.9) {
        spawnAt(pct);
        prevPct = pct;
      }
      tickParticles();
      if (raw < 1) {
        barRaf = requestAnimationFrame(barTick);
      } else {
        barFill.style.width = '100%';
        barRaf = requestAnimationFrame(drain);
      }
    }

    resizeCanvas();
    nameLoop();
    barRaf = requestAnimationFrame(barTick);

    window.addEventListener('resize', resizeCanvas);

    return () => {
      activeRef.current = false;
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(barRaf);
    };
  }, [onDone]);

  return (
    <div className="ld-scene">
      <div className="ld-stage">
        <div className="ld-flex" ref={flexRef} />
      </div>
      <div className="ld-bar-area">
        <div className="ld-bar-track">
          <canvas className="ld-bar-canvas" ref={canvasRef} />
          <div className="ld-bar-fill" ref={barFillRef} />
        </div>
        <div className="ld-label">loading</div>
      </div>
    </div>
  );
}

export default Loading;
